import { createFileRoute } from "@tanstack/react-router";
import {
  Mail,
  MessageCircle,
  Phone,
  Instagram,
  Linkedin,
  Star,
  MapPin,
  Send,
  Quote,
  ShieldCheck,
  Clock,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { site } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { testimonials } from "@/data/testimonials";

const inquirySchema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(80),
  email: z.string().trim().email("Enter a valid email").max(160),
  intake: z.string().trim().max(40).optional(),
  message: z.string().trim().min(10, "Tell us a bit more (10+ chars)").max(800),
});

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Us - WeLivdIt" },
      {
        name: "description",
        content:
          "Reach WeLivdIt on WhatsApp, email, Instagram, or LinkedIn. Based in Hyderabad. We typically reply within 48 hours.",
      },
      { property: "og:title", content: "Contact Us - WeLivdIt" },
      {
        property: "og:description",
        content: "WhatsApp, email, Instagram, LinkedIn - pick a channel and we'll reply within 48 hours.",
      },
      { property: "og:url", content: "https://welivdit.com/contact" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/contact" }],
  }),
  component: ContactPage,
});

const primaryChannels = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: site.whatsappNumber,
    hint: "Fastest response — usually instant",
    href: site.whatsapp,
    primary: true,
    actionText: "Chat on WhatsApp →",
  },
  {
    icon: Phone,
    label: "Phone Support",
    value: site.phone,
    hint: "Mon–Sat, 10am–7pm IST",
    href: `tel:${site.phoneTel}`,
    primary: false,
    actionText: "Call Us →",
  },
  {
    icon: Mail,
    label: "Email Us",
    value: site.email,
    hint: "We reply within 48 hours",
    href: `mailto:${site.email}`,
    primary: false,
    actionText: "Send Email →",
  },
];

const socialChannels = [
  { icon: Instagram, label: "Instagram", value: "@welivdit", href: site.socials.instagram },
  { icon: Linkedin, label: "LinkedIn", value: "WeLivdIt", href: site.socials.linkedin },
  {
    icon: Star,
    label: "Trustpilot",
    value: `${site.rating.score}★ · ${site.rating.count} reviews`,
    href: site.socials.trustpilot,
  },
];

function ContactPage() {
  const featured = testimonials[0];

  return (
    <main className="min-h-screen bg-background pb-20">
      {/* HERO SECTION */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute top-0 right-1/4 h-72 w-72 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="mx-auto max-w-6xl px-5 pt-16 pb-14 md:pt-24 md:pb-20 text-center md:text-left">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary border border-primary/20">
            <Sparkles className="h-3.5 w-3.5" /> Instant Support Available
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground text-balance">
            Pick a channel. <span className="text-primary">We&apos;ll be there.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Chat with us directly on WhatsApp for immediate guidance, or submit your query below to launch a direct
            pre-filled WhatsApp thread.
          </p>
        </div>
      </section>

      {/* MAIN CONTENT SPLIT GRID */}
      <section className="mx-auto max-w-6xl px-5 pt-12 md:pt-16">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT COLUMN: Channels, Testimonial, Socials, Address */}
          <div className="lg:col-span-7 space-y-10">
            {/* Primary Contact Channels */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl font-semibold tracking-tight">Direct Channels</h2>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-primary" /> Real humans, no bots
                </span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-1">
                {primaryChannels.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    className={`group relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border p-5 transition-all duration-200 hover:-translate-y-0.5 ${
                      c.primary
                        ? "bg-gradient-to-r from-primary/10 via-card to-card border-primary/40 shadow-sm hover:border-primary"
                        : "bg-card border-border hover:border-primary/40 hover:shadow-sm"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`h-12 w-12 rounded-xl grid place-items-center shrink-0 transition-transform group-hover:scale-105 ${
                          c.primary ? "bg-primary text-primary-foreground shadow-sm" : "bg-accent text-primary"
                        }`}
                      >
                        <c.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="font-display font-semibold text-base text-foreground flex items-center gap-2">
                          {c.label}
                          {c.primary && (
                            <span className="rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                              Fastest
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-medium text-foreground/90 mt-0.5">{c.value}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{c.hint}</div>
                      </div>
                    </div>

                    <span className="text-xs font-semibold text-primary inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform self-end sm:self-center">
                      {c.actionText}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Featured Student Testimonial */}
            <figure className="relative overflow-hidden rounded-3xl bg-card border border-border p-6 md:p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-2xl bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Quote className="h-5 w-5" />
                </div>
                <div className="min-w-0 space-y-3">
                  <div className="flex items-center gap-1 text-amber-500">
                    {Array.from({ length: featured.rating }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-400" />
                    ))}
                  </div>
                  <blockquote className="font-display text-base md:text-lg leading-relaxed text-foreground italic">
                    &ldquo;{featured.content}&rdquo;
                  </blockquote>
                  <figcaption className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    — {featured.name} ·{" "}
                    <a
                      href={site.socials.trustpilot}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline underline-offset-2 hover:opacity-80"
                    >
                      Verified Trustpilot Review
                    </a>
                  </figcaption>
                </div>
              </div>
            </figure>

            {/* Social Channels */}
            <div className="space-y-3">
              <h3 className="font-display text-lg font-semibold tracking-tight">Socials & Reviews</h3>
              <div className="flex flex-wrap gap-2.5">
                {socialChannels.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-foreground/90 hover:bg-accent hover:border-primary/40 transition group"
                  >
                    <c.icon className="h-4 w-4 text-primary" />
                    <span>{c.label}</span>
                    <span className="text-muted-foreground">({c.value})</span>
                    <ArrowUpRight className="h-3 w-3 text-muted-foreground group-hover:text-primary transition" />
                  </a>
                ))}
              </div>
            </div>

            {/* Registered Business Address */}
            <div className="rounded-3xl bg-card border border-border p-6 flex items-start gap-4">
              <div className="h-10 w-10 rounded-2xl bg-accent grid place-items-center text-primary shrink-0">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="text-sm space-y-1">
                <div className="font-semibold text-base text-foreground">{site.legalName}</div>
                <div className="text-xs text-muted-foreground">CIN: {site.cin}</div>
                <address className="not-italic text-xs md:text-sm text-foreground/80 leading-relaxed pt-1">
                  {site.address.line1}
                  <br />
                  {site.address.line2}
                  <br />
                  {site.address.region}
                </address>
              </div>
            </div>

            {/* Grievance Details Accordion */}
            <details className="group rounded-2xl bg-muted/30 border border-border px-5 py-4 text-xs transition">
              <summary className="flex items-center gap-3 cursor-pointer list-none select-none">
                <span className="h-7 w-7 rounded-full bg-background border border-border grid place-items-center text-primary shrink-0">
                  <ShieldCheck className="h-3.5 w-3.5" />
                </span>
                <span className="flex-1">
                  <span className="block font-semibold text-foreground">Grievance Officer - Srinath</span>
                  <span className="block text-muted-foreground">Questions or concerns? Reach out directly.</span>
                </span>
                <span className="text-muted-foreground group-open:hidden">Show</span>
                <span className="text-muted-foreground hidden group-open:inline">Hide</span>
              </summary>
              <div className="mt-3 pl-10 space-y-1 text-foreground/80 border-t border-border/60 pt-3">
                <div>
                  <span className="text-muted-foreground">Designation:</span> Founder &amp; Grievance Officer
                </div>
                <div>
                  <span className="text-muted-foreground">Contact:</span>{" "}
                  <a href={`tel:${site.phoneTel}`} className="text-primary underline">
                    {site.phone}
                  </a>
                  {" · "}
                  <a href={`mailto:${site.email}`} className="text-primary underline">
                    {site.email}
                  </a>
                </div>
                <div className="text-muted-foreground text-[11px] pt-1">
                  Plot No-57, Phase-2, JPN Nagar, Miyapur, Hyderabad, 500049.
                </div>
              </div>
            </details>
          </div>

          {/* RIGHT COLUMN: Interactive Inquiry Form */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <InquiryForm />
          </div>
        </div>
      </section>
    </main>
  );
}

function InquiryForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [intake, setIntake] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = inquirySchema.safeParse({ name, email, intake, message });
    if (!result.success) {
      toast.error(result.error.issues[0]?.message ?? "Please check the form");
      return;
    }
    setSubmitting(true);
    const { name: n, email: em, intake: it, message: msg } = result.data;
    const body = [
      `Hi WeLivdIt - I'd like to inquire about visa prep services.`,
      ``,
      `Name: ${n}`,
      `Email: ${em}`,
      it ? `Intake/University: ${it}` : null,
      ``,
      `Message:`,
      msg,
    ]
      .filter(Boolean)
      .join("\n");

    const waNumber = "916300736712";
    const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(body)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — send the pre-filled message to connect with us!");
    setSubmitting(false);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-3xl bg-card border border-border p-6 md:p-8 shadow-sm space-y-5 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl pointer-events-none" />

      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary bg-primary/10 rounded-full px-2.5 py-0.5 mb-2">
          One-Tap WhatsApp Connect
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Send an Inquiry</h2>
        <p className="mt-1 text-xs md:text-sm text-muted-foreground leading-relaxed">
          Fill in your details below and we&apos;ll open a pre-filled WhatsApp conversation for instant response.
        </p>
      </div>

      <div className="space-y-4 pt-1">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-xs font-semibold">
            Your Full Name <span className="text-primary">*</span>
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Aarav Sharma"
            maxLength={80}
            required
            className="rounded-xl border-border bg-background focus:ring-primary/20 text-sm h-11"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs font-semibold">
            Email Address <span className="text-primary">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            maxLength={160}
            required
            className="rounded-xl border-border bg-background focus:ring-primary/20 text-sm h-11"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="intake" className="text-xs font-semibold">
            Intake / University <span className="text-muted-foreground font-normal">(Optional)</span>
          </Label>
          <Input
            id="intake"
            value={intake}
            onChange={(e) => setIntake(e.target.value)}
            placeholder="Fall 2026 · ASU"
            maxLength={40}
            className="rounded-xl border-border bg-background focus:ring-primary/20 text-sm h-11"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <Label htmlFor="message" className="text-xs font-semibold">
              Message <span className="text-primary">*</span>
            </Label>
            <span className="text-[10px] text-muted-foreground">{message.length}/800</span>
          </div>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Interview date, services you're interested in, or specific concerns..."
            rows={4}
            maxLength={800}
            required
            className="rounded-xl border-border bg-background focus:ring-primary/20 text-sm resize-none"
          />
        </div>
      </div>

      <div className="pt-2 space-y-3">
        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-12 rounded-full bg-gradient-cta text-primary-foreground font-semibold shadow-glow hover:opacity-95 transition flex items-center justify-center gap-2"
        >
          <Send className="h-4 w-4" />
          <span>Send via WhatsApp →</span>
        </Button>

        <p className="text-[11px] text-center text-muted-foreground">
          We usually reply within a few hours on business days.
        </p>
      </div>
    </form>
  );
}
